import React, { useContext, useEffect, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import { useExpanded, useRowSelect, useTable } from 'react-table';

import classNames from 'classnames';

import { Icon, Loader, Table } from 'semantic-ui-react';

import iconBarChartThin from '../../img/icon-bar-chart-thin.svg';

import '../../css/reports/ReportTable.less';
import '../../css/reports/ReportTreeSelectionTable.less';

import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';

import PopupService from '../../services/PopupService';
import ReportOverrideService from '../../services/reports/ReportOverrideService';
import ReportScoreService from '../../services/reports/ReportScoreService';
import ReportStandardsService from '../../services/reports/ReportStandardsService';
import ReportTableService from '../../services/reports/ReportTableService';

const ReportTreeSelectionTable = observer((initProps) => {
  const {
    shouldExpandAllRowsOnInit,
    t
  } = initProps;

  const {
    reportCourseManager, reportIdentityManager, reportOverrideManager,
    reportStandardsManager, reportTableManager
  } = useContext(MobXProviderContext);

  const {
    allowReportHideRowsResetAllAsUnselectedAction,
    allowReportHideRowsResetAllToOriginalSelectionsAction
  } = reportOverrideManager;

  const [loadingReportTable, setLoadingReportTable] = useState(true);

  const IndeterminateCheckbox = SatCoreComponent('IndeterminateCheckbox');
  const ReportResourceTitleCell = SatCoreComponent('ReportResourceTitleCell');
  const ZeroStateCard = SatCoreComponent('ZeroStateCard');

  const {
    activeReportType,
    isCourseReport,
    isFacultyAdminReport,
    isFacultyClassroomReport,
    isStandardsReport,
    reportInfoClassNames
  } = reportIdentityManager;

  // TODO unused // const hasBetaPermission = userManager.hasSatelliteBetaAccessUserPermission;

  /** @see https://react-table-v7-docs.netlify.app/docs/quick-start */
  const generateColumnsConfig = () => {
    const cellExpanderConfig = {
      /** CONFIG: `cell-expander` */
      Cell: (props) => renderRowExpanderCell(props),
      Header: (props) => renderHeaderRowExpanderCell(props),
      className: `cell-expander ${reportInfoClassNames}`,
      id: 'expander'
    };
    const reportColumnsConfig = [
      {
      /** CONFIG: `cell-resource-title` */
        Cell: (props) => renderResourceTitleCell(props),
        Header: t(`${activeReportType}TableHeader`),
        accessor: (props) => {
          if (isCourseReport) {
            return {
              name: props.elementName
            };
          } else if (isStandardsReport) {
            return {
              description: props.description,
              name: props.name
            };
          }
        },
        className: `cell-after-expander cell-resource-title ${reportInfoClassNames}`
      }
    ];
    return [
      cellExpanderConfig, {
        Header: 'header-parent-row',
        columns: reportColumnsConfig
      }
    ];
  };

  /** RENDER: `cell-expander` (header) */
  const renderHeaderRowExpanderCell = (props) => {
    const { getToggleAllRowsExpandedProps, isAllRowsExpanded } = props;
    return PopupService.renderPopup({
      className: `header-row-expander-popup ${reportInfoClassNames} report-table-header-popup`,
      content: (
        <div>
          {t(isAllRowsExpanded ? 'descriptionForCollapseAllRows' : 'descriptionForExpandAllRows')}
        </div>
      ),
      offset: [-11, 0],
      position: 'top right',
      trigger: (
        <>
          <span {...getToggleAllRowsExpandedProps({
            title: undefined // removes native placeholder rollover
          })}>
            <Icon name={isAllRowsExpanded ? 'caret down' : 'caret right'} />
          </span>
        </>
      )
    });
  };

  /** RENDER: `cell-expander` (body) */
  const renderRowExpanderCell = (props) => {
    const { row } = props;
    return (
      row.canExpand ? (
        <span {...row.getToggleRowExpandedProps({
          className: `${row['className']} ${reportInfoClassNames}`,
          title: undefined // removes native placeholder rollover
          // TODO useful if we ever implement row depth padding // style: { paddingLeft: `${row.depth * 16}px` }
        })}>
          <Icon name={row.isExpanded ? 'caret down' : 'caret right'} />
        </span>
      ) : null
    );
  };

  /** RENDER: `cell-resource-title` */
  const renderResourceTitleCell = (props) => {
    return (
      <ReportResourceTitleCell {...initProps} {...props} />
    );
  };

  const {
    flatRows,
    getTableBodyProps,
    getTableProps,
    getToggleAllRowsSelectedProps,
    headerGroups,
    prepareRow,
    rows,
    toggleRowExpanded,
    toggleRowSelected
  } = useTable({
    columns: React.useMemo(() => generateColumnsConfig(), []),
    data: reportTableManager.reportTableData,
    selectSubRows: false
  }, useExpanded, useRowSelect, (hooks) => {
    hooks.visibleColumns.push((columns) => [
      // add checkbox column
      {
        Cell: ({ row } = {}) => {
          return (
            <div>
              <IndeterminateCheckbox {...row.getToggleRowSelectedProps({
                indeterminate: false, // we want these checkboxes to be either checked or unchecked, never partially checked (-)
                title: undefined // removes native placeholder rollover
              })} />
            </div>
          );
        },
        Header: '',
        className: `cell-checkbox ${reportInfoClassNames}`,
        id: 'selection'
      },
      ...columns
    ]);
  });

  /** equivalent to componentDidMount(), i.e. only called after initial render */
  useEffect(() => {
    (async () => {
      setLoadingReportTable(true);
      reportTableManager.setShouldReinitializeReportTreeSelectionTableRows(true);
      setLoadingReportTable(false);
    })();
  }, []);

  // eslint-disable-next-line no-unused-vars
  const [_headerParentRow, headerRow] = headerGroups;

  const renderReportTableZeroState = ({ customTranslationKey = 'noReportTableRowsToSelectForHide' } = {}) => {
    return (
      <ZeroStateCard {...initProps}
        additionalClassNames={reportInfoClassNames}
        icon={iconBarChartThin}
        translationKey={t(customTranslationKey)} />
    );
  };

  /** initialize expanded and selected rows when the component mounts; save on unmount */
  useEffect(() => {
    if (flatRows?.length && reportTableManager.shouldReinitializeReportTreeSelectionTableRows) {
      const { isCourseReport } = reportIdentityManager;
      const tableRowIdProp = isCourseReport ? 'elementId' : 'id';
      const previousSelectedRowMap = reportTableManager.confirmedSelectedHiddenReportTableRowMap;
      const hasPreviousSelections = previousSelectedRowMap && previousSelectedRowMap.size > 0;

      // Initialize expanded and selected rows based on the current state or defaults
      flatRows.forEach((tableRow) => {
        // TODO remove
        // const shouldHideTableRow = ReportTableService.shouldHideTableRow(tableRow, { fromReportTreeSelectionTable: true });
        // tableRow.meta = {
        //   ...(tableRow.meta || {}),
        //   isExcludedFromSelection: shouldHideTableRow
        // };
        toggleRowExpanded(tableRow.id, !!shouldExpandAllRowsOnInit);

        // Check if this row was previously selected, otherwise default to false (unchecked)
        const rowId = tableRow.original?.[tableRowIdProp];
        let shouldSelect = false; // Default to unchecked

        if (hasPreviousSelections && rowId) {
          // If we have previous selections, restore them
          shouldSelect = previousSelectedRowMap.get(rowId) === true;
        }

        toggleRowSelected(tableRow.id, shouldSelect);
      });
      reportTableManager.setShouldReinitializeReportTreeSelectionTableRows(false);
    }
    // Save reportTableSelectedRows state only when component unmounts
    return () => {
      if (flatRows?.length && reportTableManager.shouldUpdateReportTreeSelectionTableRows) {
        // User confirmed changes, save the current selections
        ReportTableService.setReportTableSelectedRows([...flatRows], {
          fromReportTreeSelectionTable: true
        });
        reportTableManager.setShouldUpdateReportTreeSelectionTableRows(false);
        reportTableManager.setShouldReinitializeReportTableExpandedRows(true);
      } else {
        // User canceled, keep the existing selections (do not clear)
        // TODO remove // reportTableManager.clearConfirmedSelectedHiddenReportTableRowMap();
        if (reportTableManager.shouldUpdateReportTreeSelectionTableRows) {
          reportTableManager.setShouldUpdateReportTreeSelectionTableRows(false);
        }
        // Do not reinitialize expanded rows on cancel - preserve current expanded state
        reportTableManager.setShouldReinitializeReportTableExpandedRows(false);
      }
    };
  }, [flatRows, shouldExpandAllRowsOnInit]);

  const renderReportTable = () => {
    return (
      <div className={`ReportTreeSelectionTable report-table-container ${reportInfoClassNames}`}>
        <div className={`report-table-top-container ${reportInfoClassNames}`}>
          {/* note: we should avoid putting additional components in `report-table-top-container` unless absolutely necessary */}
          {/* --- */}
          {/* **avoid** - anything put here will likely need extensive css manipulation to appear to be in the 'correct' position */}
          {/* use ReportHeader instead of this container if possible */}
          {/* --- */}
        </div>
        <div className={`report-table-wrapper ${reportInfoClassNames}`}>
          <Table {...getTableProps()}
            celled className={`report-table ${reportInfoClassNames}`} singleLine striped>
            {renderTableHeader()}
            {renderTableBody()}
          </Table>
        </div>
      </div>
    );
  };

  const handleResetAllToOriginalSelections = () => {
    if (flatRows?.length) {
      const { isCourseReport } = reportIdentityManager;
      const tableRowIdProp = isCourseReport ? 'elementId' : 'id';
      const previousSelectedRowMap = reportTableManager.confirmedSelectedHiddenReportTableRowMap;
      const hasPreviousSelections = previousSelectedRowMap && previousSelectedRowMap.size > 0;

      flatRows.forEach((tableRow) => {
        toggleRowExpanded(tableRow.id, !!shouldExpandAllRowsOnInit);

        // Restore original selections if they exist, otherwise uncheck all
        const rowId = tableRow.original?.[tableRowIdProp];
        let shouldSelect = false;
        if (hasPreviousSelections && rowId) {
          shouldSelect = previousSelectedRowMap.get(rowId) === true;
        }
        toggleRowSelected(tableRow.id, shouldSelect);
      });
    }
  };

  const handleResetAllAsUnselected = () => {
    if (flatRows?.length) {
      flatRows.forEach((tableRow) => {
        toggleRowExpanded(tableRow.id, !!shouldExpandAllRowsOnInit);
        toggleRowSelected(tableRow.id, false);
      });
    }
  };

  const renderHeaderCheckboxResetAllToOriginalSelectionsAction = () => {
    return (
      <div className='header-checkbox-reset-all-action reset-to-original-selections-action'
        onClick={handleResetAllToOriginalSelections}
        onKeyUp={(event) => {
          if (event?.key === 'Enter' || event?.key === ' ') {
            handleResetAllToOriginalSelections();
          }
        }}
        role='button'
        tabIndex={0}>
        {t('resetAllToOriginalSelections')}
      </div>
    );
  };

  const renderHeaderCheckboxResetAllAsUnselectedAction = () => {
    return (
      <div className='header-checkbox-reset-all-action reset-all-as-unselected-action'
        onClick={handleResetAllAsUnselected}
        onKeyUp={(event) => {
          if (event?.key === 'Enter' || event?.key === ' ') {
            handleResetAllAsUnselected();
          }
        }}
        role='button'
        tabIndex={0}>
        {t('resetAllAsUnselected')}
      </div>
    );
  };

  const renderTableHeader = () => {
    const hasSomeNotSelected = !!flatRows.some((tableRow) => {
      const isExcludedFromSelection = tableRow.meta?.isExcludedFromSelection;
      return !isExcludedFromSelection && !tableRow.isSelected;
    });
    const toggleAllRowsSelectedProps = getToggleAllRowsSelectedProps({
      title: undefined // removes native placeholder rollover
    }) || {};
    return (
      <Table.Header>
        <Table.Row className={`report-table-header-checkbox-controls-row ${reportInfoClassNames}`}>
          <Table.HeaderCell className='cell-top-header-checkbox' colSpan={headerRow.headers.length}>
            <div className='header-checkbox-container'>
              <div className='header-checkbox-label-wrapper'>
                <IndeterminateCheckbox {...toggleAllRowsSelectedProps}
                  checked={!hasSomeNotSelected}
                  indeterminate={false}
                  onChange={(event) => {
                    // when the header checkbox is manually triggered, also expand all rows
                    // to ensure the header checkbox is properly set to checked/unchecked.
                    // i.e. when some rows are collapsed, the header checkbox does not always update properly when manually toggling it
                    const shouldExpandAll = true;
                    const shouldSelectAll = !!(event?.target?.checked);
                    if (flatRows?.length) {
                      flatRows.forEach((tableRow) => {
                        toggleRowExpanded(tableRow.id, shouldExpandAll);
                        toggleRowSelected(tableRow.id, shouldSelectAll);
                      });
                    }
                  }} />
                <div className={classNames('header-checkbox-select-all-label', {
                  checked: !hasSomeNotSelected,
                  indeterminate: false
                })}>
                  {t('selectAllReportTableRows')}
                </div>
              </div>
              {allowReportHideRowsResetAllToOriginalSelectionsAction && renderHeaderCheckboxResetAllToOriginalSelectionsAction()}
              {allowReportHideRowsResetAllAsUnselectedAction && renderHeaderCheckboxResetAllAsUnselectedAction()}
            </div>
          </Table.HeaderCell>
        </Table.Row>
        <Table.Row {...headerRow.getHeaderGroupProps({
          className: headerRow['className']
        })}>
          {headerRow.headers.map((headerColumn) => {
            return (
              <Table.HeaderCell key={`header-column-${headerColumn.id}`}
                {...headerColumn.getHeaderProps({
                  className: headerColumn['className']
                })}>
                {headerColumn.render('Header')}
              </Table.HeaderCell>
            );
          })}
        </Table.Row>
      </Table.Header>
    );
  };

  const renderTableBody = () => {
    return (
      <Table.Body {...getTableBodyProps()}>
        {rows.map((row, _bodyRowIndex) => {
          prepareRow(row);
          const { elementType } = row.original;
          const expandableRowClassName = ReportTableService.getExpandableRowClassNameIfApplicable(elementType, { row });

          const rowProps = row.getRowProps();
          const isRollupAverageRow = row.original.id === 'rollupAverageRow';
          let reportRowClassNames = rowProps.className || '';
          reportRowClassNames += `${reportRowClassNames ? ' ' : ''}${isRollupAverageRow ? 'rollup-average-row' : ''}`;
          reportRowClassNames += `${reportRowClassNames ? ' ' : ''}${expandableRowClassName.trim()}`.trim();
          reportRowClassNames += `${reportRowClassNames ? ' ' : ''}${reportInfoClassNames}`;

          const shouldHideTableRow = ReportTableService.shouldHideTableRow(row, { fromReportTreeSelectionTable: true });

          if (shouldHideTableRow !== row.meta?.isExcludedFromSelection) {
            row.meta = {
              ...(row.meta || {}),
              isExcludedFromSelection: shouldHideTableRow
            };
          }

          // for each cell, determine the value we should use
          const cells = row.cells.map((cell, _bodyCellIndex) => {
            let cellClassNames = cell.column['className'] || '';
            let value;
            if (expandableRowClassName) {
              cellClassNames += expandableRowClassName;
            }
            const isRowExemptFromHavingValue = ReportTableService.isRowExemptFromHavingValue(row);
            if (!isRowExemptFromHavingValue) {
              if (cellClassNames.includes('result')) {
                const isFacultyAverageCell = cell?.column?.id?.toLowerCase?.()?.includes?.('average');
                if (isStandardsReport && !isFacultyAverageCell && (
                  cell.row.original.id === 'rollupAverageRow' || cell.row.canExpand)
                ) {
                  value = ReportStandardsService.getCustomReportFacultyResultCellValue(
                    { ...cell, flatRows }) || null;
                  cell = { ...cell, value };
                } else if (isFacultyAverageCell && cell.row.original.hasRollup) {
                  value = cell.row.original.overallRollup?.averageScore;
                  cell = { ...cell, value };
                }
              }
            }
            return cell;
          });
          row.cells = cells;

          return !shouldHideTableRow && (
            <Table.Row key={`body-row-${row.id}`} {...row.getRowProps()}
              className={reportRowClassNames}>
              {row.cells.map((cell, _bodyCellIndex) => {
                // for each cell, determine the classNames we should use
                let cellClassNames = cell.column['className'] || '';
                if (expandableRowClassName) {
                  cellClassNames += expandableRowClassName;
                }
                const isRowExemptFromHavingValue = ReportTableService.isRowExemptFromHavingValue(row);
                if (!isRowExemptFromHavingValue) {
                  if (cellClassNames.includes('result')) {
                    const isFacultyAverageCell = cell?.column?.id?.toLowerCase?.()?.includes?.('average');
                    if (!isFacultyAverageCell && (
                      cell.row.original.id === 'rollupAverageRow' || cell.row.canExpand)
                    ) {
                      cellClassNames += ` ${ReportScoreService.getReportScoreCellClassName(cell)}`;
                    } else if (isFacultyAverageCell && cell.row.original.hasRollup) {
                      cellClassNames += ` ${ReportScoreService.getReportScoreCellClassName(cell)}`;
                    } else {
                      cellClassNames += ` ${ReportScoreService.getReportScoreCellClassName(cell)}`;
                    }
                    if (isCourseReport && isFacultyClassroomReport && cell.value?.excludeCount) {
                      cellClassNames += ' modified';
                    }
                  }
                }
                return (
                  <Table.Cell key={`cell-${cell.column.id}`}
                    {...cell.getCellProps({
                      className: cellClassNames
                    })}>
                    {cell.render('Cell', cell.value >= 0 ? { value: cell.value } : undefined)}
                  </Table.Cell>
                );
              })}
            </Table.Row>
          );
        })}
      </Table.Body>
    );
  };

  const { loadingReportCourseNames, selectedReportCourseContentItemId } = reportCourseManager;
  const { forceShowNoDataZeroState, hasReportResponseJsonError } = reportIdentityManager;
  const { allowUseCmapId } = reportOverrideManager;
  const { selectedReportCmapContentItemId } = reportStandardsManager;
  const { reportTableData } = reportTableManager;

  const shouldUseCmapId = ReportOverrideService.shouldUseCmapId();

  let shouldShowReportTableZeroState;
  if (isStandardsReport && !allowUseCmapId && isFacultyAdminReport && !selectedReportCourseContentItemId) {
    shouldShowReportTableZeroState = true;
  } else if (isStandardsReport && shouldUseCmapId && isFacultyAdminReport && !selectedReportCmapContentItemId) {
    shouldShowReportTableZeroState = true;
  } else if (forceShowNoDataZeroState) {
    shouldShowReportTableZeroState = true;
  } else if (hasReportResponseJsonError) {
    shouldShowReportTableZeroState = true;
  } else if (isCourseReport && isFacultyAdminReport && !selectedReportCourseContentItemId) {
    shouldShowReportTableZeroState = true;
  }

  if (shouldShowReportTableZeroState) {
    return renderReportTableZeroState();
  } else if (/* loadingReportView || */loadingReportTable || loadingReportCourseNames) {
    return <Loader active />;
  } else if (reportTableData?.length) {
    return renderReportTable();
  } else {
    return renderReportTableZeroState();
  }
});
export default ReportTreeSelectionTable;

SatCoreRegister('ReportTreeSelectionTable', ReportTreeSelectionTable);
